


































































































































































































































































































































































































import { Component, Prop, Watch, Vue } from 'vue-property-decorator';
import SidePanel from '@/components/Shared/SidePanel.vue';
import { KpiTargetsModule } from '@/store/modules/kpiTargets';
import { AgencySettingsModule } from '@/store/modules/agencySettings';
import { getFiscalYearOptions } from '@/lib/fiscalYears';
import KpiTargetDto from '@/models/KpiTargets/KpiTargetDto';
import CreateKpiTargetRequest from '@/models/KpiTargets/CreateKpiTargetRequest';
import UpdateKpiTargetRequest from '@/models/KpiTargets/UpdateKpiTargetRequest';
import {
  KPI_CODES, PERIOD_TYPES, VISIBILITY_OPTIONS, ENTITY_TYPES,
  getKpiDefinition, KpiCodeDefinition
} from '@/models/KpiTargets/KpiCodeRegistry';

@Component({
  name: 'KpiTargetSidePanel',
  components: { SidePanel }
})
export default class KpiTargetSidePanel extends Vue {
  @Prop({ default: false })
  visible!: boolean;

  @Prop({ default: null })
  target!: KpiTargetDto | null;

  valid = false;
  showFromDatePicker = false;
  showToDatePicker = false;
  scopeLevel = 'agency';
  selectedEntityType: number | null = null;
  bulkMode = false;
  rpFiscalYearFilter: number | null = null;

  form = {
    branchId: null as string | null,
    deskId: null as string | null,
    agencyUserId: null as string | null,
    agencyUserIds: [] as string[],
    kpiCode: '',
    periodType: 0,
    targetValue: 0,
    effectiveFrom: '',
    effectiveTo: null as string | null,
    // Dimension filters (object arrays for return-object)
    activityCategories: [] as any[],
    positions: [] as any[],
    locationTypes: [] as any[],
    skills: [] as any[],
    qualifications: [] as any[],
    // Visibility
    visibilityType: 1,
    visibilityGroupId: null as string | null,
    // Custom reporting period
    reportingPeriodId: null as string | null,
  };

  get isEdit(): boolean {
    return this.target !== null;
  }

  get loading(): boolean {
    return KpiTargetsModule.loading;
  }

  get branches() {
    return AgencySettingsModule.branches;
  }

  get desksForBranch() {
    if (!this.form.branchId) return [];
    const branch = AgencySettingsModule.branches.find(b => b.id === this.form.branchId);
    return branch ? branch.desks || [] : [];
  }

  get agencyUserItems() {
    return AgencySettingsModule.agencyUsers.map(u => ({
      id: u.id,
      displayName: u.person ? u.person.firstName + ' ' + u.person.lastName : u.emailAddress,
    }));
  }

  get entityTypeItems() {
    return ENTITY_TYPES;
  }

  get filteredKpiCodeItems() {
    if (this.selectedEntityType !== null && this.selectedEntityType !== undefined) {
      return KPI_CODES.filter(k => k.entityType === this.selectedEntityType);
    }
    return KPI_CODES;
  }

  get periodTypes() {
    return PERIOD_TYPES;
  }

  get visibilityOptions() {
    return VISIBILITY_OPTIONS;
  }

  get agencyGroupItems() {
    return AgencySettingsModule.agencyGroups;
  }

  get reportingPeriods() {
    return AgencySettingsModule.reportingPeriods;
  }

  get rpFiscalYearItems(): number[] {
    return getFiscalYearOptions();
  }

  get filteredReportingPeriods() {
    const list = this.rpFiscalYearFilter
      ? this.reportingPeriods.filter((p: any) => p.fiscalYear === this.rpFiscalYearFilter)
      : this.reportingPeriods;
    return [...list]
      .sort((a: any, b: any) => {
        if (a.fiscalYear !== b.fiscalYear) return b.fiscalYear - a.fiscalYear;
        return a.periodNumber - b.periodNumber;
      })
      .map((p: any) => ({
        id: p.id,
        displayText: p.label ? p.label : 'P' + p.periodNumber + ' FY' + p.fiscalYear,
      }));
  }

  get selectedReportingPeriodDates(): string {
    if (!this.form.reportingPeriodId) return '';
    const rp = this.reportingPeriods.find((p: any) => p.id === this.form.reportingPeriodId);
    if (!rp) return '';
    const start = rp.startDate ? rp.startDate.substring(0, 10) : '';
    const end = rp.endDate ? rp.endDate.substring(0, 10) : '';
    return start + ' to ' + end;
  }

  get activityCategoryItems() {
    return AgencySettingsModule.activityCategories;
  }

  get positionItems() {
    return AgencySettingsModule.positions;
  }

  get locationTypeItems() {
    return AgencySettingsModule.locationTypes;
  }

  get skillItems() {
    return AgencySettingsModule.skills;
  }

  get qualificationItems() {
    return AgencySettingsModule.qualifications;
  }

  // Dimension visibility computed properties
  get selectedKpiDefinition(): KpiCodeDefinition | undefined {
    return getKpiDefinition(this.form.kpiCode);
  }

  get showActivityCategories(): boolean {
    const def = this.selectedKpiDefinition;
    return def ? def.allowActivityCategories : false;
  }

  get showPositions(): boolean {
    const def = this.selectedKpiDefinition;
    return def ? def.allowPositions : false;
  }

  get showLocationTypes(): boolean {
    const def = this.selectedKpiDefinition;
    return def ? def.allowLocationTypes : false;
  }

  get showSkills(): boolean {
    const def = this.selectedKpiDefinition;
    return def ? def.allowSkills : false;
  }

  get showQualifications(): boolean {
    const def = this.selectedKpiDefinition;
    return def ? def.allowQualifications : false;
  }

  get kpiDescription(): string {
    const def = this.selectedKpiDefinition;
    return def ? def.description : '';
  }

  get saveButtonText(): string {
    if (this.isEdit) return 'Update';
    if (this.bulkMode && this.form.agencyUserIds.length > 0) {
      return 'Create for ' + this.form.agencyUserIds.length + ' Users';
    }
    return 'Create';
  }

  get unitLabel(): string {
    const def = this.selectedKpiDefinition;
    if (!def) return '';
    switch (def.unit) {
      case 'count': return 'Count';
      case 'percent': return 'Percent';
      case 'currency': return 'Currency';
      case 'hours': return 'Hours';
      case 'days': return 'Days';
      default: return def.unit;
    }
  }

  @Watch('visible')
  onVisibleChanged(val: boolean) {
    if (val) {
      this.ensureReferenceData();
      this.populateForm();
    }
  }

  async ensureReferenceData() {
    if (AgencySettingsModule.activityCategories.length === 0) {
      AgencySettingsModule.getActivityCategories();
    }
    if (AgencySettingsModule.positions.length === 0) {
      AgencySettingsModule.getPositions();
    }
    if (AgencySettingsModule.locationTypes.length === 0) {
      AgencySettingsModule.getLocationTypes();
    }
    if (AgencySettingsModule.skills.length === 0) {
      AgencySettingsModule.getSkills();
    }
    if (AgencySettingsModule.qualifications.length === 0) {
      AgencySettingsModule.getQualifications();
    }
    if (AgencySettingsModule.agencyGroups.length === 0) {
      AgencySettingsModule.getAgencyGroups();
    }
    if (AgencySettingsModule.reportingPeriods.length === 0) {
      AgencySettingsModule.getReportingPeriods();
    }
  }

  populateForm() {
    if (this.target) {
      // Edit mode
      this.selectedEntityType = this.target.entityType;
      this.form.branchId = this.target.branchId;
      this.form.deskId = this.target.deskId;
      this.form.agencyUserId = this.target.agencyUserId;
      this.form.kpiCode = this.target.kpiCode;
      this.form.periodType = this.target.periodType;
      this.form.targetValue = this.target.targetValue;
      this.form.effectiveFrom = this.target.effectiveFrom ? this.target.effectiveFrom.substring(0, 10) : '';
      this.form.effectiveTo = this.target.effectiveTo ? this.target.effectiveTo.substring(0, 10) : null;

      // Dimension filters
      this.form.activityCategories = this.target.activityCategories || [];
      this.form.positions = this.target.positions || [];
      this.form.locationTypes = this.target.locationTypes || [];
      this.form.skills = this.target.skills || [];
      this.form.qualifications = this.target.qualifications || [];

      // Visibility
      this.form.visibilityType = this.target.visibilityType || 1;
      this.form.visibilityGroupId = this.target.visibilityGroupId;

      // Custom reporting period
      this.form.reportingPeriodId = this.target.reportingPeriodId;
      if (this.target.reportingPeriod) {
        this.rpFiscalYearFilter = this.target.reportingPeriod.fiscalYear || null;
      } else {
        this.rpFiscalYearFilter = null;
      }

      // Set scope level based on existing data
      if (this.target.agencyUserId) {
        this.scopeLevel = 'user';
      } else if (this.target.deskId) {
        this.scopeLevel = 'desk';
      } else if (this.target.branchId) {
        this.scopeLevel = 'branch';
      } else {
        this.scopeLevel = 'agency';
      }
    } else {
      // Create mode - reset
      this.selectedEntityType = null;
      this.scopeLevel = 'agency';
      this.bulkMode = false;
      this.form.branchId = null;
      this.form.deskId = null;
      this.form.agencyUserId = null;
      this.form.agencyUserIds = [];
      this.form.kpiCode = '';
      this.form.periodType = 0;
      this.form.targetValue = 0;
      this.form.effectiveFrom = '';
      this.form.effectiveTo = null;
      this.form.activityCategories = [];
      this.form.positions = [];
      this.form.locationTypes = [];
      this.form.skills = [];
      this.form.qualifications = [];
      this.form.visibilityType = 1;
      this.form.visibilityGroupId = null;
      this.form.reportingPeriodId = null;
      this.rpFiscalYearFilter = null;
    }
  }

  onScopeLevelChanged() {
    this.form.branchId = null;
    this.form.deskId = null;
    this.form.agencyUserId = null;
    this.form.agencyUserIds = [];
    this.bulkMode = false;
  }

  onBranchChanged() {
    this.form.deskId = null;
  }

  onEntityTypeChanged() {
    // Clear KPI code if it no longer matches the selected entity type
    if (this.form.kpiCode && this.selectedEntityType !== null) {
      const def = KPI_CODES.find(k => k.code === this.form.kpiCode);
      if (def && def.entityType !== this.selectedEntityType) {
        this.form.kpiCode = '';
        this.form.periodType = 0;
        this.form.activityCategories = [];
        this.form.positions = [];
        this.form.locationTypes = [];
        this.form.skills = [];
        this.form.qualifications = [];
      }
    }
  }

  onKpiCodeChanged(code: string) {
    const kpi = KPI_CODES.find(k => k.code === code);
    if (kpi) {
      this.form.periodType = kpi.defaultPeriod;
    }
    // Clear all dimension fields when KPI changes
    this.form.activityCategories = [];
    this.form.positions = [];
    this.form.locationTypes = [];
    this.form.skills = [];
    this.form.qualifications = [];
  }

  onPeriodTypeChanged() {
    if (this.form.periodType === 5) {
      // Custom period — clear manual dates, load reporting periods
      this.form.effectiveFrom = '';
      this.form.effectiveTo = null;
      this.form.reportingPeriodId = null;
      if (this.reportingPeriods.length === 0) {
        AgencySettingsModule.getReportingPeriods();
      }
    } else {
      // Non-custom — clear reporting period selection
      this.form.reportingPeriodId = null;
    }
  }

  onRpFiscalYearChanged() {
    // Clear selected period if it no longer matches the fiscal year filter
    if (this.rpFiscalYearFilter && this.form.reportingPeriodId) {
      const rp = this.reportingPeriods.find((p: any) => p.id === this.form.reportingPeriodId);
      if (rp && rp.fiscalYear !== this.rpFiscalYearFilter) {
        this.form.reportingPeriodId = null;
      }
    }
  }

  onReportingPeriodChanged() {
    // No additional action needed — dates are displayed read-only via computed
  }

  formatReportingPeriodItem(item: any): string {
    if (item.label) return item.label;
    return 'P' + item.periodNumber + ' FY' + item.fiscalYear;
  }

  async save() {
    if (this.isEdit && this.target) {
      const request = new UpdateKpiTargetRequest();
      request.updatedAt = this.target.updatedAt;
      request.branchId = this.scopeLevel === 'branch' || this.scopeLevel === 'desk' ? this.form.branchId : null;
      request.deskId = this.scopeLevel === 'desk' ? this.form.deskId : null;
      request.agencyUserId = this.scopeLevel === 'user' ? this.form.agencyUserId : null;
      request.kpiCode = this.form.kpiCode;
      request.periodType = this.form.periodType;
      request.effectiveFrom = this.form.periodType === 5 ? '' : this.form.effectiveFrom;
      request.effectiveTo = this.form.periodType === 5 ? null : this.form.effectiveTo;
      request.targetValue = this.form.targetValue;
      request.reportingPeriodId = this.form.periodType === 5 ? this.form.reportingPeriodId : null;

      // Dimension IDs
      request.activityCategoryIds = this.form.activityCategories.map(c => c.id);
      request.positionIds = this.form.positions.map(p => p.id);
      request.locationTypeIds = this.form.locationTypes.map(lt => lt.id);
      request.skillIds = this.form.skills.map(s => s.id);
      request.qualificationIds = this.form.qualifications.map(q => q.id);

      // Visibility
      request.visibilityType = this.form.visibilityType;
      request.visibilityGroupId = this.form.visibilityType === 3 ? this.form.visibilityGroupId : null;

      const result = await KpiTargetsModule.updateKpiTarget({ id: this.target.id, request });
      if (result) {
        this.$emit('saved');
      }
    } else if (this.bulkMode && this.scopeLevel === 'user') {
      // Bulk create for multiple users
      const bulkRequest = {
        agencyUserIds: this.form.agencyUserIds,
        kpiCode: this.form.kpiCode,
        periodType: this.form.periodType,
        effectiveFrom: this.form.periodType === 5 ? '' : this.form.effectiveFrom,
        effectiveTo: this.form.periodType === 5 ? null : this.form.effectiveTo,
        targetValue: this.form.targetValue,
        reportingPeriodId: this.form.periodType === 5 ? this.form.reportingPeriodId : null,
        activityCategoryIds: this.form.activityCategories.map(c => c.id),
        positionIds: this.form.positions.map(p => p.id),
        locationTypeIds: this.form.locationTypes.map(lt => lt.id),
        skillIds: this.form.skills.map(s => s.id),
        qualificationIds: this.form.qualifications.map(q => q.id),
        visibilityType: this.form.visibilityType,
        visibilityGroupId: this.form.visibilityType === 3 ? this.form.visibilityGroupId : null,
      };
      const result = await KpiTargetsModule.bulkCreateKpiTargets(bulkRequest);
      if (result) {
        this.$emit('saved');
      }
    } else {
      const request = new CreateKpiTargetRequest();
      request.branchId = this.scopeLevel === 'branch' || this.scopeLevel === 'desk' ? this.form.branchId : null;
      request.deskId = this.scopeLevel === 'desk' ? this.form.deskId : null;
      request.agencyUserId = this.scopeLevel === 'user' ? this.form.agencyUserId : null;
      request.kpiCode = this.form.kpiCode;
      request.periodType = this.form.periodType;
      request.effectiveFrom = this.form.periodType === 5 ? '' : this.form.effectiveFrom;
      request.effectiveTo = this.form.periodType === 5 ? null : this.form.effectiveTo;
      request.targetValue = this.form.targetValue;
      request.reportingPeriodId = this.form.periodType === 5 ? this.form.reportingPeriodId : null;

      // Dimension IDs
      request.activityCategoryIds = this.form.activityCategories.map(c => c.id);
      request.positionIds = this.form.positions.map(p => p.id);
      request.locationTypeIds = this.form.locationTypes.map(lt => lt.id);
      request.skillIds = this.form.skills.map(s => s.id);
      request.qualificationIds = this.form.qualifications.map(q => q.id);

      // Visibility
      request.visibilityType = this.form.visibilityType;
      request.visibilityGroupId = this.form.visibilityType === 3 ? this.form.visibilityGroupId : null;

      const result = await KpiTargetsModule.createKpiTarget(request);
      if (result) {
        this.$emit('saved');
      }
    }
  }
}
